import {
  getRelativePosition,
  useGetMultiplePrint,
  useUpdateMultiplePrint,
} from "@/api/multipleprint/multipleprint";
import { Separator } from "@/components/ui/separator";
import { Multipleprint } from "@/lib/types";
import { useParams, useSearchParams } from "react-router-dom";

import { useGetAttachmentsByDocId } from "@/api/attachment";
import { useGetAllAuthors } from "@/api/author/author";
import {
  appendNoDuplicateRecordNumberRefine,
  multipleprintSchema,
} from "@/api/multipleprint/multipleprint-validation";
import * as AF from "@/components/form-items/AbstractForm";
import {
  FourDimensionalInput,
  TwoDimensionalInput,
} from "@/components/form-items/DimensionsInput";
import { ModifiableList } from "@/components/form-items/ModifiableList";
import { DoubleMultilingualField } from "@/components/form-items/MultilingualField";
import LinksManagement from "@/components/LinksManagement";
import AddExemplaireModal from "@/components/modal/AddExemplaireModal";
import AddFileModal from "@/components/modal/AddFileModal";
import { toast } from "@/hooks/use-toast";
import { useDashboardParams } from "@/hooks/useDashboardParams";
import { getMainLanguage } from "@/i18n";
import { FormLayout } from "@/layout/FormLayout";
import { computeFieldMultipleprintSummary } from "@/lib/computeFields";
import { standardSchemaResolver } from "@hookform/resolvers/standard-schema";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

const schema = appendNoDuplicateRecordNumberRefine(
  multipleprintSchema.passthrough(),
);

const EditMultipleprint = ({
  multipleprint,
}: {
  multipleprint: Multipleprint;
}) => {
  const defaultValues = useMemo(() => {
    return {
      ...multipleprint,
      relatedAuthorIds: multipleprint.relatedAuthor.map((author) => author.id),
    };
  }, [multipleprint]);

  const form = useForm({
    defaultValues,
    resolver: standardSchemaResolver(schema),
    reValidateMode: "onSubmit",
  });

  useEffect(() => {
    form.reset(defaultValues);
  }, [defaultValues, form]);

  const { data: attachments } = useGetAttachmentsByDocId(multipleprint.id);
  // TODO: apply MultiSelect search
  const { data: authors } = useGetAllAuthors({
    where: {},
    order: { artistesAuteursDescriptionNom: "asc" },
    take: 50,
    skip: 0,
  });

  const queryClient = useQueryClient();
  const [, setSearchParam] = useSearchParams();

  const multipleprintUpdater = useUpdateMultiplePrint();

  const { relativePosition } = useDashboardParams<Multipleprint>({
    statusField: "estampesMultiplesPublicationStatus",
    resourceName: "multipleprint",
    relativePosition: {
      currentId: multipleprint.id,
      positionFetcher: getRelativePosition,
    },
  });

  const { t } = useTranslation();

  const handleResume = (data: Multipleprint) => {
    return computeFieldMultipleprintSummary(data, {
      language: getMainLanguage(),
      lineBreak: true,
    });
  };

  const shouldDisplayVolume =
    form.watch("estampesMultiplesDescriptionDimensionsProfondeur") || false;

  return (
    <FormLayout
      onSave={(status) =>
        form.handleSubmit(
          (values) => {
            multipleprintUpdater.mutate(
              {
                ...(values as Multipleprint),
                ...(status && { estampesMultiplesPublicationStatus: status }),
              },
              {
                onSuccess: () => {
                  toast({
                    title: t(
                      status === undefined
                        ? "autoSave"
                        : "recordSavedToastTitle",
                    ),
                    variant: "success",
                    description: t("multipleprintSavedToastDescription"),
                  });
                  form.reset({}, { keepValues: true });
                },
              },
            );
          },
          (invalid) => {
            if (invalid.ventesDescriptionNumeroVente != undefined)
              setSearchParam({ tab: "description" });
          },
        )()
      }
      invalidateOnChange={() => {
        queryClient.invalidateQueries({
          queryKey: ["multipleprint", multipleprint.id],
        });
        queryClient.invalidateQueries({ queryKey: ["multipleprints"] });
        queryClient.invalidateQueries({
          queryKey: ["attachment", { docId: multipleprint.id }],
        });
      }}
      publicationStatus={multipleprint.estampesMultiplesPublicationStatus}
      reference={multipleprint.estampesMultiplesDescriptionNumeroEnregistrement}
      title={multipleprint.estampesMultiplesDescriptionTitre}
      id={multipleprint.id}
      type="multipleprint"
      relativePosition={relativePosition}
      picturesData={
        attachments && {
          pictures: attachments,
          type: "multipleprint",
        }
      }
      form={form}
    >
      <Separator />
      <AF.AbstractForm
        form={form}
        tabs={[
          { value: "description", label: t("tab_description") },
          { value: "dimensions-poids", label: t("multipleDimensionsPoids") },
          { value: "epreuveTirage", label: t("epreuves") },
          { value: "sources", label: t("sources") },
          { value: "files", label: t("fichiers") },
          { value: "exemplaires", label: t("exemplaires") },
          { value: "links", label: t("linkedEntries") },
        ]}
      >
        <AF.Tab value="description" className="flex-col">
          <AF.Row className="items-start">
            <AF.Col>
              <AF.Text name="estampesMultiplesDescriptionNumeroEnregistrement" />
              <AF.MultiSelectField
                name="relatedAuthorIds"
                options={
                  authors?.map((author) => ({
                    label: `${author.artistesAuteursDescriptionPrenom} ${author.artistesAuteursDescriptionNom}`,
                    value: author.id,
                  })) ?? []
                }
                label={t("estampesMultiplesDescriptionAuteurArtiste")}
                placeholder={t("select")}
              />
              <AF.RadioGroup
                resettable={true}
                items={[
                  {
                    value: "estampesMultiplesDescriptionAuteurArtisteAttribueA",
                  },
                  { value: "estampesMultiplesDescriptionAuteurArtisteAnonyme" },
                  { value: "estampesMultiplesDescriptionAuteurArtisteDApres" },
                  {
                    value:
                      "estampesMultiplesDescriptionAuteurArtisteDansLeGoutDe",
                  },
                  {
                    value:
                      "estampesMultiplesDescriptionAuteurArtisteEntourageDe",
                  },
                  { value: "estampesMultiplesDescriptionAuteurAtelierDe" },
                ]}
              />
              <AF.Text name="estampesMultiplesDescriptionTitre" />
              <AF.Text name="estampesMultiplesDescriptionSousTitre" />
              <AF.RadioGroup
                items={[
                  { value: "estampesMultiplesDescriptionTitreSansTitre" },
                  { value: "estampesMultiplesDescriptionTitreInconnu" },
                  { value: "estampesMultiplesDescriptionTitreIncertin" },
                ]}
              />
              <Separator />
              {
                // TODO: OEUVRES DANS ENSEMBLE
              }
              <AF.Row>
                <ModifiableList
                  name="estampesMultiplesDescriptionType"
                  listKey="liste-types-oeuvres"
                />
                <AF.Checkbox
                  name="estampesMultiplesDescriptionOeuvreDoubleFace"
                  align={true}
                />
              </AF.Row>
              <AF.Row>
                <AF.Text name="estampesMultiplesDescriptionDateDeCreation" />
                <AF.Checkbox
                  name="estampesMultiplesDescriptionDateDeCreationInconnue"
                  align={true}
                />
              </AF.Row>

              <ModifiableList
                name="estampesMultiplesDescriptionEditeur"
                listKey="liste-multipleprint-editeur"
              />
              <ModifiableList
                name="estampesMultiplesDescriptionFabricantImprimeur"
                listKey="liste-multipleprint-imprmeur"
              />
            </AF.Col>
            <AF.Col>
              <AF.RichText
                name="estampesMultiplesDescriptionResume"
                computeAction={handleResume}
              />
              <AF.RichText name="estampesMultiplesDescriptionNotesTravail" />
              <AF.RichText name="estampesMultiplesDescriptionNotesPubliques" />
            </AF.Col>
          </AF.Row>
          <Separator />
          <AF.Col>
            <span>{t("forms.multilingual-fields.indication")}</span>

            <AF.Row className="items-start">
              <DoubleMultilingualField
                className="w-full"
                // fixed={getMainLanguage()}
                render={(lang) => (
                  <AF.Col>
                    <ModifiableList
                      label={t("estampesMultiplesDescriptionTechnique")}
                      name={`estampesMultiplesDescriptionTechnique.${lang}`}
                      listKey={`liste-techniques`}
                      lang={lang}
                    />
                    <ModifiableList
                      label={t("estampesMultiplesDescriptionSupportMateriau")}
                      name={`estampesMultiplesDescriptionSupportMateriau.${lang}`}
                      listKey={`liste-supports`}
                      lang={lang}
                    />
                    <ModifiableList
                      label={t(
                        "estampesMultiplesDescriptionSignatureLocalisation",
                      )}
                      name={`estampesMultiplesDescriptionSignatureLocalisation.${lang}`}
                      listKey="liste-signatures"
                      lang={lang}
                    />
                    <AF.Text name="estampesMultiplesDescriptionSignatureTranscription" />
                    <AF.Text
                      label={t("estampesMultiplesDescriptionInscriptionsDos")}
                      name={`estampesMultiplesDescriptionInscriptionsDos.${lang}`}
                    />
                  </AF.Col>
                )}
              />
            </AF.Row>
          </AF.Col>
        </AF.Tab>
        <AF.Tab value="dimensions-poids">
          <AF.Col>
            <FourDimensionalInput
              parentName="estampesMultiplesDescriptionDimensionsOeuvre"
              needSurface={!shouldDisplayVolume}
              needVolume={shouldDisplayVolume}
            />
            <AF.RadioGroup
              resettable={true}
              items={[
                {
                  value:
                    "estampesMultiplesDescriptionDimensionsOeuvreVerifiees",
                },
                {
                  value:
                    "estampesMultiplesDescriptionDimensionsOeuvreInconnues",
                },
                {
                  value:
                    "estampesMultiplesDescriptionDimensionsOeuvreDouteuses",
                },
              ]}
            />
            <AF.Checkbox name="estampesMultiplesDescriptionDimensionsProfondeur" />
            <TwoDimensionalInput parentName="estampesMultiplesDescriptionDimensionsImage" />
            <AF.RadioGroup
              resettable={true}
              items={[
                {
                  value: "estampesMultiplesDescriptionDimensionsImageVerifiees",
                },
                {
                  value: "estampesMultiplesDescriptionDimensionsImageInconnues",
                },
                {
                  value: "estampesMultiplesDescriptionDimensionsImageDouteuses",
                },
              ]}
            />
            <AF.Row className="items-start-start">
              <AF.Text
                name="estampesMultiplesDescriptionPoidsOeuvre"
                unit="kg"
                className="w-20"
              />{" "}
            </AF.Row>
            <AF.Text
              name="estampesMultiplesDescriptionDimensionsOeuvreRaw"
              label={t("estampesMultiplesDescriptionDimensionsOeuvreRaw")}
              sublabel={t(
                "estampesMultiplesDescriptionDimensionsOeuvreRawSublabel",
              )}
            />
          </AF.Col>
        </AF.Tab>
        <AF.Tab value="epreuveTirage">
          <AF.Col>
            <AF.RichText name="estampesMultiplesDescriptionDescriptionComplete" />
          </AF.Col>
        </AF.Tab>
        <AF.Tab value="sources">
          <AF.RichText name="sources" />
        </AF.Tab>
        <AF.Tab
          value="files"
          headerExtras={<AddFileModal id={multipleprint.id} />}
        >
          <AF.OthersFilesTable docId={multipleprint.id} name="diversfiles" />
        </AF.Tab>
        <AF.Tab
          value="exemplaires"
          headerExtras={<AddExemplaireModal id={multipleprint.id} />}
        >
          <AF.ExemplairesTable docId={multipleprint.id} name="exemplaires" />
        </AF.Tab>
        <AF.Tab value="links">
          <LinksManagement
            data={multipleprint}
            id={multipleprint.id}
            type="multipleprint"
            configuration={{
              fields: [
                {
                  name: "links",
                  type: "links",
                  label: "Liens",
                  links: [
                    {
                      name: "relatedArtwork",
                      label: t("entryartworks"),
                      type: "link-simple",
                    },
                    {
                      name: "relatedMultipleprint",
                      label: t("entrymultipleprints"),
                      type: "link-simple",
                    },
                    {
                      name: "multipleprintInExhibition",
                      label: t("entryexhibitions"),
                      type: "link-complex",
                      fields: [
                        {
                          name: "exhibition.expositionsDescriptionNumeroExpo",
                          label: t(
                            "exhibitionDansEstampesMultiplesDescriptionNumeroExpo",
                          ),
                          type: "text",
                          className: "w-full",
                          disabled: true,
                        },
                        {
                          name: "exhibition.expositionsDescriptionResume",
                          label: t(
                            "exhibitionDansEstampesMultiplesDescriptionResume",
                          ),
                          type: "richtext",
                          className: "w-full",
                          disabled: true,
                        },
                        {
                          name: "expositionsDansEstampesMultiplesDescriptionNumero",
                          label: t(
                            "exhibitionDansEstampesMultiplesDescriptionNumero",
                          ),
                          type: "text",
                          className: "w-full",
                        },
                        {
                          name: "expositionsDansEstampesMultiplesDescriptionExemplaireExpose",
                          label: t(
                            "exhibitionDansEstampesMultiplesDescriptionExemplaireExpose",
                          ),
                          type: "text",
                          className: "w-full",
                        },
                      ],
                    },
                    {
                      name: "multipleprintInBibliography",
                      label: t("entrybibliographies"),
                      type: "link-complex",
                      fields: [
                        {
                          name: "bibliography.bibliographieDescriptionNumeroBibliographie",
                          label: t(
                            "bibliographieDescriptionNumeroBibliographie",
                          ),
                          type: "text",
                          className: "w-full",
                          disabled: true,
                        },
                        {
                          name: "bibliography.bibliographieDescriptionResumeLong",
                          label: t("bibliographieDescriptionResumeLong"),
                          type: "richtext",
                          className: "w-full",
                          disabled: true,
                        },
                        {
                          name: "bibliographieDansEstampesMultiplesDescriptionReproduitPage",
                          type: "text",
                          label: t(
                            "bibliographieDansEstampesMultiplesDescriptionReproduitPage",
                          ),
                          className: "w-full",
                        },
                        {
                          name: "bibliographieDansEstampesMultiplesDescriptionCitePage",
                          type: "text",
                          label: t(
                            "bibliographieDansEstampesMultiplesDescriptionCitePage",
                          ),
                          className: "w-full",
                        },
                        {
                          name: "bibliographieDansEstampesMultiplesDescriptionPhotographe",
                          type: "text",
                          label: t(
                            "bibliographieDansEstampesMultiplesDescriptionPhotographe",
                          ),
                          className: "w-full",
                        },
                        {
                          name: "bibliographieDansEstampesMultiplesDescriptionReproductionCoul",
                          type: "checkbox",
                          label: t(
                            "bibliographieDansEstampesMultiplesDescriptionReproductionCoul",
                          ),
                          className: "w-full",
                        },
                        {
                          name: "bibliographieDansEstampesMultiplesDescriptionExemplaireRepr",
                          type: "text",
                          label: t(
                            "bibliographieDansEstampesMultiplesDescriptionExemplaireRepr",
                          ),
                          className: "w-full",
                        },
                      ],
                    },
                    {
                      name: "multipleprintInAuction",
                      label: t("entryauctions"),
                      type: "link-complex",
                      fields: [
                        {
                          name: "auction.ventesDescriptionNumeroVente",
                          label: t(
                            "auctionDansEstampesMultiplesDescriptionNumeroVente",
                          ),
                          type: "text",
                          className: "w-full",
                          disabled: true,
                        },
                        {
                          name: "auction.ventesDescriptionResume",
                          label: t(
                            "auctionDansEstampesMultiplesDescriptionResume",
                          ),
                          type: "richtext",
                          className: "w-full",
                          disabled: true,
                        },
                        {
                          name: "ventesDansEstampesMultiplesDescriptionNumeroLot",
                          type: "text",
                          label: t(
                            "auctionDansEstampesMultiplesDescriptionNumeroLot",
                          ),
                          className: "w-full",
                        },
                        {
                          name: "ventesDansEstampesMultiplesDescriptionReproduitPage",
                          type: "text",
                          label: t(
                            "auctionDansEstampesMultiplesDescriptionReproduitPage",
                          ),
                          className: "w-full",
                        },
                        {
                          name: "ventesDansEstampesMultiplesDescriptionEstimationBasse",
                          type: "text",
                          label: t(
                            "auctionDansEstampesMultiplesDescriptionEstimationBasse",
                          ),
                          className: "w-full",
                        },
                        {
                          name: "ventesDansEstampesMultiplesDescriptionEstimationHaute",
                          type: "text",
                          label: t(
                            "auctionDansEstampesMultiplesDescriptionEstimationHaute",
                          ),
                          className: "w-full",
                        },
                        {
                          name: "ventesDansEstampesMultiplesDescriptionVendu",
                          type: "checkbox",
                          label: t(
                            "auctionDansEstampesMultiplesDescriptionVendu",
                          ),
                          className: "w-full",
                        },
                        {
                          name: "ventesDansEstampesMultiplesDescriptionResultatNonListe",
                          type: "checkbox",
                          label: t(
                            "auctionDansEstampesMultiplesDescriptionResultatNonListe",
                          ),
                          className: "w-full",
                        },
                        {
                          name: "ventesDansEstampesMultiplesDescriptionNonVenu",
                          type: "checkbox",
                          label: t(
                            "auctionDansEstampesMultiplesDescriptionNonVenu",
                          ),
                          className: "w-full",
                        },
                        {
                          name: "ventesDansEstampesMultiplesDescriptionRetire",
                          type: "checkbox",
                          label: t(
                            "auctionDansEstampesMultiplesDescriptionRetire",
                          ),
                          className: "w-full",
                        },
                        {
                          name: "ventesDansEstampesMultiplesDescriptionMiseAPrix",
                          type: "inputSelectCurrency",
                          label: t(
                            "auctionDansEstampesMultiplesDescriptionMiseAPrix",
                          ),
                          className: "w-full",
                        },
                        {
                          name: "ventesDansEstampesMultiplesDescriptionPrixMarteau",
                          type: "inputSelectCurrency",
                          label: t(
                            "auctionDansEstampesMultiplesDescriptionPrixMarteau",
                          ),
                          className: "w-full",
                        },
                        {
                          name: "ventesDansEstampesMultiplesDescriptionPrixAvecFrais",
                          type: "inputSelectCurrency",
                          label: t(
                            "auctionDansEstampesMultiplesDescriptionPrixAvecFrais",
                          ),
                          className: "w-full",
                        },
                        {
                          name: "ventesDansEstampesMultiplesDescriptionExemplaireVendu",
                          type: "text",
                          label: t(
                            "auctionDansEstampesMultiplesDescriptionExemplaireVendu",
                          ),
                          className: "w-full",
                        },
                      ],
                    },
                  ],
                },
              ],
            }}
          />
        </AF.Tab>
      </AF.AbstractForm>
    </FormLayout>
  );
};

export const EditMultiplePrintPage = () => {
  const { id } = useParams();

  const { data, isLoading } = useGetMultiplePrint(id);

  return (
    <>
      {data && <EditMultipleprint key={data.id} multipleprint={data} />}
      {!data && !isLoading && <h2 className="p-4">estampeNexistePasLabel</h2>}
    </>
  );
};
